import { request } from './client';

/**
 * Visits somebody is expected to make.
 *
 * Until now an audit existed only once an auditor opened one, which made "we visited thirty
 * stores" true and "we were supposed to visit thirty-six" unanswerable. A plan is the half
 * that was missing.
 */

const base = '/api/v1/plans';

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export type PlanStatus = 'Planned' | 'Started' | 'Completed' | 'Missed' | 'Cancelled';

export interface Plan {
  id: string;

  locationId: string;
  locationCode: string;
  locationName: string;
  city: string | null;

  formId: string;
  formName: string;
  formCode: string;

  assignedToUserId: string;
  assignedToName: string;
  plannedByName: string;

  from: string;
  to: string;

  status: PlanStatus;

  /** The period has run out and nothing was handed in. Worked out on the server. */
  isOverdue: boolean;

  /** The audit it became, once somebody started it. */
  auditId: string | null;

  note: string | null;
  cancelledReason: string | null;
}

export interface PlanList {
  rows: Plan[];
  /** More matched than came back. Said out loud rather than quietly truncated. */
  truncated: boolean;
}

/** A store that could not be planned, and why. Reported rather than swallowed. */
export interface PlanRefusal {
  locationId: string;
  locationCode: string;
  locationName: string;
  code: string;
}

export interface PlansCreated {
  created: Plan[];
  refused: PlanRefusal[];
}

export interface PlanFilter {
  mine?: boolean;
  open?: boolean;
  status?: PlanStatus;
  locationId?: string;
  assignedToUserId?: string;
  from?: string;
  to?: string;
}

export interface CreatePlans {
  locationIds: string[];
  formId: string;
  assignedToUserId: string;
  from: string;
  to: string;
  note?: string;
  /** Whether to tell the assignee. Defaults to yes on the server. */
  notify?: boolean;
}

export interface Auditor {
  id: string;
  name: string;
}

function query(filter: PlanFilter): string {
  const parts = new URLSearchParams();

  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== null && value !== '') parts.set(key, String(value));
  }

  const q = parts.toString();
  return q ? `?${q}` : '';
}

export const plans = {
  list: (filter: PlanFilter = {}) => request<PlanList>(`${base}${query(filter)}`),

  /**
   * Who a visit can be assigned to.
   *
   * Served by the planning routes rather than by user administration: the person planning a
   * month holds plans.manage and usually not users.manage, so that route would answer 403
   * and leave the box silently empty.
   */
  auditors: () => request<Auditor[]>(`${base}/auditors`),

  create: (body: CreatePlans) => request<PlansCreated>(base, json('POST', body)),

  /** Opens the visit. Returns the audit it became — the screen then goes there. */
  start: (id: string) =>
    request<{ planId: string; auditId: string }>(`${base}/${id}/start`, json('POST', {})),

  cancel: (id: string, reason: string) =>
    request<Plan>(`${base}/${id}/cancel`, json('POST', { reason })),

  change: (
    id: string,
    body: { from?: string; to?: string; assignedToUserId?: string; note?: string; notify?: boolean },
  ) =>
    request<Plan>(`${base}/${id}`, json('PUT', body)),
};

/** Planned against done, for a period. */
export interface PlanTally {
  /** Who or where this row counts. Empty on the total. */
  name: string;
  planned: number;
  completed: number;
  missed: number;
  cancelled: number;
  /** Still open and still inside its period. */
  open: number;
  /** Open, and past the period's end. */
  overdue: number;
  /** Completed as a share of what was expected. Cancelled plans leave the denominator. */
  rate: number | null;
}

export interface PlanCompliance {
  total: PlanTally;
  byAuditor: PlanTally[];
  byRegion: PlanTally[];
}

export const planCompliance = (from?: string, to?: string) => {
  const parts = new URLSearchParams();
  if (from) parts.set('from', from);
  if (to) parts.set('to', to);

  const q = parts.toString();
  return request<PlanCompliance>(`/api/v1/reports/plan-compliance${q ? `?${q}` : ''}`);
};
